import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { LimitsInputComponent } from '@app/shared/inputs/limits-input/limits-input.component';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { first, skip, asapScheduler } from 'rxjs';

@Component({
  selector: 'app-body-weight',
  templateUrl: './body-weight.component.html',
  styleUrls: ['./body-weight.component.css']
})
export class BodyWeightComponent implements OnInit {

  @ViewChild('childDiagnosisComponent') childDiagnosisComponent: RadioInputGroupComponent;
  @ViewChild('childLimitsComponent') childLimitsComponent: LimitsInputComponent;

  @Input() indication: IndicationDefinition;
  bodyWeightDiagnosisIndication: IndicationDefinition;
  bodyWeightLimitsIndication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];

  childDiagnosisSelectionModel: SelectionsModel;
  childLimitsSelectionModel: SelectionsModel[];

  maleLowerLimitsDefault = 90.6;
  femaleLowerLimitsDefault = 77.5;

  maleLowerLimitsVariableName = 'maleLowerLimits';
  femaleLowerLimitsVariableName = 'femaleLowerLimits';

  bodyWeightInputOptions: InputOption[][] = [
    // diagnosis inputs
    [
      {name: 'DO', displayName: 'Diagnosed Overweight Only', order: 1, defaultValue: 'DO'},
      {name: 'MC', displayName: 'Meets Criteria (All Patients)', order: 2, defaultValue: 'DO'},
      {name: 'DU', displayName: 'Diagnosed Overweight + Undiagnosed (Meets Criteria)', order: 3, defaultValue: 'DO'}
    ],
    // limits inputs
    [
      {name: this.maleLowerLimitsVariableName, displayName: 'Male Lower Limits', order: 1, defaultValue: this.maleLowerLimitsDefault, maxlength: 5, details: '(>=)'},
      {name: this.femaleLowerLimitsVariableName, displayName: 'Female Lower Limits', order: 2, defaultValue: this.femaleLowerLimitsDefault, maxlength: 5, details: '(>=)'}
    ]
  ];

  constructor(
    private sharedService: SharedService
  ) { }

  ngOnInit() {

    this.bodyWeightDiagnosisIndication = Object.assign({}, this.indication);
    this.bodyWeightDiagnosisIndication.inputOptions = this.bodyWeightInputOptions[0];

    this.bodyWeightLimitsIndication = Object.assign({}, this.indication);
    this.bodyWeightLimitsIndication.inputOptions = this.bodyWeightInputOptions[1];

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });
    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {
        if (this.indication.selections.some(i => i.controlName === 'diagnosis')) {
          this.childDiagnosisComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'diagnosis').selectionValue);
        }
        if (this.childLimitsComponent && this.indication.selections.some(i => i.controlName === this.maleLowerLimitsVariableName)) {
          this.childLimitsComponent.overrideInputValue(this.maleLowerLimitsVariableName, this.indication.selections.find(i => i.controlName === this.maleLowerLimitsVariableName).selectionValue);
        }
        if (this.childLimitsComponent && this.indication.selections.some(i => i.controlName === this.femaleLowerLimitsVariableName)) {
          this.childLimitsComponent.overrideInputValue(this.femaleLowerLimitsVariableName, this.indication.selections.find(i => i.controlName === this.femaleLowerLimitsVariableName).selectionValue);
        }
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  childSharedRadioControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childDiagnosisSelectionModel = selectionModel;
    // reset limits if not visible but form has errors
    if (!this.showCriteriaSection()) {
      this.setLimitsDefaults();
    }
    this.updateSelections();
  }

  childSharedTextControlSelectionChangedHandler(selections: SelectionsModel[]) {
    this.childLimitsSelectionModel = selections;
    this.updateSelections();
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.childDiagnosisComponent.overrideInputValue('DO');
      this.setLimitsDefaults();
      this.updateSelections();
    });
  }

  setLimitsDefaults() {
    if (this.childLimitsComponent) {
      this.childLimitsComponent.overrideInputValue(this.maleLowerLimitsVariableName, this.maleLowerLimitsDefault);
      this.childLimitsComponent.overrideInputValue(this.femaleLowerLimitsVariableName, this.femaleLowerLimitsDefault);
    }
  }

  showCriteriaSection(): boolean {
    const visible = this.childDiagnosisSelectionModel && 
    !vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(this.childDiagnosisSelectionModel.selectionValue);
    return visible;
  }

  updateSelections(): void {
    let currentSelections: SelectionsModel[] = [];

    if (this.childDiagnosisSelectionModel) {
      currentSelections.push(this.childDiagnosisSelectionModel);
    }

    if (this.childLimitsSelectionModel) {
      currentSelections = currentSelections.concat(this.childLimitsSelectionModel);
    }
    
    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }

}
